import {PlatformIdentityService} from '../../../services/auth/identity/platform-identity.service';
import {Component, EventEmitter, inject, Input, OnDestroy, Output, ViewEncapsulation} from '@angular/core';
import {MaterialModule} from 'src/app/material.module';
import {Router, RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {ClientService} from "../../../services/auth/client.service";
import {MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService} from "@azure/msal-angular";
import {InteractionType} from "@azure/msal-browser";
import {IClientOptions} from "../../../models/client-options.model";
import {SidebarService} from "../../../services/layout/sidebar.service";
import {ApiKeysComponent} from "../../../components/client-settings/api-keys/api-keys.component";
import {ClientSettingsComponent} from "../../../components/client-settings/client-settings.component";
import {ToastService} from "../../../services/layout/toast.service";
import {environment} from "../../../../environments/environment";
import {SelectableEntity} from "../../../services/auth/entity-selection-dialog.service";
import {ClientSelectionDialogService} from "../../../services/layout/client-selection-dialog.service";
import {ClientStateService} from "../../../services/auth/client-state.service";
import {Observable} from "rxjs";
import {ClientPreparationStatusComponent} from '../../../components/client-settings/client-preparation-status.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, CommonModule, MaterialModule, ClientPreparationStatusComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnDestroy {
    public readonly clientService = inject(ClientService);
    private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG, { optional: true })!;
    private readonly msalService = inject(MsalService, { optional: true })!;
    private readonly identity = inject(PlatformIdentityService);
    private readonly router = inject(Router);
    private readonly toastService = inject(ToastService);
    private readonly sidebarService = inject(SidebarService);
    private readonly clientSelectionDialog = inject(ClientSelectionDialogService);
    private readonly clientStateService = inject(ClientStateService);

    clientLoading$: Observable<boolean> = this.clientStateService.clientLoading$;
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();


  /**
   * Computed property that returns clients sorted alphabetically by name
   */
  get sortedClients(): IClientOptions[] {
    return [...this.clientService.clients].sort((a, b) => a.name.localeCompare(b.name));
  }


  ngOnDestroy(): void {
    // No subscriptions to clean up
  }

  logout() {
    if (this.identity.auth0) {
      this.clientService.resetIdentityDerivedState();
      this.identity.auth0.logout().catch(() => this.toastService.showError('Sign-out could not be completed. Please try again.'));
      return;
    }
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.msalService.logoutPopup({
        account: this.msalService.instance.getActiveAccount(),
      });
    } else {
      this.msalService.logoutRedirect({
        account: this.msalService.instance.getActiveAccount(),
      });
    }
  }

  updateClient(client: IClientOptions) {
    this.clientStateService.setClientLoading(true, 'route-loading');
    this.clientService.setClientAsync(client.id).then(() => {
      this.navigateToActiveProduct();
    });
  }

  openClientSettings() {
    this.sidebarService.open(ClientSettingsComponent, { client: this.clientService.client });
  }

  openApiKeys() {
    const currentClient = this.clientService.client;
    if (currentClient) {
      this.sidebarService.open(ApiKeysComponent, { client: currentClient });
    } else {
      this.toastService.showError('No client selected');
    }
  }

  openConnections(): void {
      this.router.navigateByUrl('/connections');
  }

  openDomains(): void {
      this.router.navigateByUrl('/domains');
  }

  openUsers(): void {
    this.router.navigateByUrl('/settings/users');
  }

  openProviders(): void {
    this.router.navigateByUrl('/connect/settings');
  }

  get isInternalAdmin(): boolean {
    return this.identity.isPlatformAdmin;
  }

    protected readonly environment = environment;

  getLogo(): string {
    if (this.clientService.client && this.clientService.client.logo && this.clientService.client.logo.length > 0) {
        return this.clientService.client.logo;
    }
    return "/assets/images/profile/user-1.jpg";
  }

    /**
     * Shows the client selector dialog
     * This provides a more comprehensive way to select clients with search functionality
     */
    showClientSelector(): void {
        // Force a refresh of all clients to ensure we have all available clients
        this.clientService.forceRefreshClients();

        // Show a loading toast while clients are being refreshed
        this.toastService.showInfo('Loading all available clients...');

        // Wait a moment for clients to refresh before showing the dialog
        setTimeout(() => {
            if (this.clientService.clients.length === 0) {
                this.toastService.showError('No clients available');
                this.clientStateService.setClientLoading(false, 'route-loading');
                return;
            }

            console.log(`Showing client selector with ${this.clientService.clients.length} clients`);

            const selectableClients = this.getSelectableClients();

            this.clientSelectionDialog.selectClient(
                selectableClients,
                'Select Client',
                'Please select a client to continue:',
                () => this.refreshSelectableClients()
            ).subscribe(selectedClientId => {
                if (!selectedClientId) {
                    this.clientStateService.setClientLoading(false, 'route-loading');
                    return;
                }

                this.clientStateService.setClientLoading(true, 'route-loading');
                this.clientService.setClientAsync(selectedClientId).then(() => {
                    this.navigateToActiveProduct();
                }).catch(error => {
                    console.error('Failed to select client:', error);
                    this.clientStateService.setClientLoading(false, 'route-loading');
                });
            });
        }, 1000); // Wait 1 second for clients to refresh
    }

    private getSelectableClients(): SelectableEntity[] {
        return this.clientService.clients.map(client => ({
            id: client.id,
            name: client.name,
            description: client.description,
            logo: client.logo,
            enabledProducts: client.enabledProducts
        }));
    }

    private async refreshSelectableClients(): Promise<SelectableEntity[]> {
        this.clientService.refreshClientsAsync(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.getSelectableClients();
    }

  /**
   * Navigates to the page for the active product
   */
  private navigateToActiveProduct(): void {
    const clientId = this.clientService.client?.id;
    const lastPath = clientId ? localStorage.getItem(`shine_last_page_${clientId}`) : null;
    const product = this.clientService.getActiveProduct();

    const navigateTo = (url: string) => {
      this.router.navigateByUrl(url).then(() => this.clientStateService.setClientLoading(false, 'route-loading'));
    };

    if (lastPath) {
      navigateTo(lastPath);
    } else {
        switch (product) {
            case 'Shine Enrich':
                navigateTo('/enrich/settings');
                break;
            case 'Shine Connect':
                navigateTo('/connect/workflows');
                break;
            case 'Shine Webinars':
                navigateTo('/webinars');
                break;
            case 'Shine Translations':
                navigateTo('/translations');
                break;
            default:
                navigateTo('/ai/dashboard');
                break;
        }
    }
  }
}
